import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import {
  TotalCarsDto,
  RevenueSummaryDto,
  OccupancyDto,
  RecentTransactionDto,
  MonthlyStatsDto
} from '../../models/dashboard-builder.model';

@Injectable({
  providedIn: 'root'
})
export class ApiDashboardBuilderService {
  private http = inject(HttpClient);

  // ================= Normal Functions =================

  // ================= API Functions =================

  /**
   * Fetches Total Cars information from API
   * ดึงข้อมูลจำนวนรถทั้งหมดที่ลงทะเบียนและกำลังทำงานตามช่วงเวลา
   */
  getTotalCarsData(siteId: string, startDate?: string, endDate?: string): Observable<TotalCarsDto> {
    const mockData: TotalCarsDto = {
      active_cars: siteId === 'site-A' ? 1420 : siteId === 'site-B' ? 980 : 1205,
      registered_cars: siteId === 'site-A' ? 1800 : siteId === 'site-B' ? 1500 : 1650,
      percentage_active: siteId === 'site-A' ? 78.8 : siteId === 'site-B' ? 65.3 : 73.0
    };

    // Simulate different results if a date range is applied
    if (startDate && endDate) {
      mockData.active_cars = Math.round(mockData.active_cars * 0.95);
      mockData.percentage_active = Number(((mockData.active_cars / mockData.registered_cars) * 100).toFixed(1));
    }

    console.log(`[API Get] getTotalCarsData for site ${siteId} (Dates: ${startDate} to ${endDate}) - Response:`, mockData);
    
    return of(mockData).pipe(
      delay(800),
      tap(res => console.log(`[API Log] TotalCarsData successfully loaded:`, res))
    );
  }

  /**
   * Fetches Revenue Summary information from API
   * ดึงข้อมูลรายได้สรุปตามช่วงเวลา ทั้งส่วนของ EV และค่าจอดรถปกติ
   */
  getRevenueSummaryData(siteId: string, startDate?: string, endDate?: string): Observable<RevenueSummaryDto> {
    const mockData: RevenueSummaryDto = {
      total_revenue: siteId === 'site-A' ? 342500 : siteId === 'site-B' ? 210800 : 276600,
      ev_revenue: siteId === 'site-A' ? 84500 : siteId === 'site-B' ? 52300 : 68400,
      parking_revenue: siteId === 'site-A' ? 258000 : siteId === 'site-B' ? 158500 : 208200
    };

    if (startDate && endDate) {
      mockData.total_revenue = Math.round(mockData.total_revenue * 0.88);
      mockData.parking_revenue = Math.round(mockData.parking_revenue * 0.88);
      mockData.ev_revenue = mockData.total_revenue - mockData.parking_revenue;
    }

    console.log(`[API Get] getRevenueSummaryData for site ${siteId} (Dates: ${startDate} to ${endDate}) - Response:`, mockData);

    return of(mockData).pipe(
      delay(800),
      tap(res => console.log(`[API Log] RevenueSummaryData successfully loaded:`, res))
    );
  }

  /**
   * Fetches Occupancy data from API
   * ดึงข้อมูลปริมาณการเข้าใช้งานช่องจอดตามช่วงเวลา
   */
  getOccupancyData(siteId: string, startDate?: string, endDate?: string): Observable<OccupancyDto> {
    const mockData: OccupancyDto = {
      total_spots: siteId === 'site-A' ? 500 : siteId === 'site-B' ? 350 : 425,
      occupied_spots: siteId === 'site-A' ? 412 : siteId === 'site-B' ? 218 : 315,
      occupancy_rate: siteId === 'site-A' ? 82.4 : siteId === 'site-B' ? 62.2 : 74.1
    };

    if (startDate && endDate) {
      mockData.occupied_spots = Math.round(mockData.occupied_spots * 0.9);
      mockData.occupancy_rate = Number(((mockData.occupied_spots / mockData.total_spots) * 100).toFixed(1));
    }

    console.log(`[API Get] getOccupancyData for site ${siteId} (Dates: ${startDate} to ${endDate}) - Response:`, mockData);

    return of(mockData).pipe(
      delay(800),
      tap(res => console.log(`[API Log] OccupancyData successfully loaded:`, res))
    );
  }

  /**
   * Fetches Recent Transactions list from API
   * ดึงข้อมูลประวัติการจอดและชำระเงินล่าสุดตามช่วงเวลา
   */
  getRecentTransactionsData(siteId: string, startDate?: string, endDate?: string): Observable<RecentTransactionDto[]> {
    const mockData: RecentTransactionDto[] = [
      { id: 1, license_plate: '3กข 1234', timestamp: '2 mins ago', amount: 40, payment_type: 'Rabbit Line Pay' },
      { id: 2, license_plate: 'กจ 5678', timestamp: '5 mins ago', amount: 60, payment_type: 'PromptPay' },
      { id: 3, license_plate: '1มง 9999', timestamp: '12 mins ago', amount: 120, payment_type: 'Credit Card' },
      { id: 4, license_plate: 'ชห 4321', timestamp: '20 mins ago', amount: 40, payment_type: 'Cash' }
    ];

    if (siteId === 'site-B') {
      mockData[0].license_plate = '5กค 9876';
      mockData[0].amount = 80;
      mockData[1].license_plate = 'วพ 2468';
      mockData[1].amount = 30;
    }

    if (startDate && endDate) {
      // Modify mock timestamps to reflect the historical date query
      mockData.forEach((t, i) => {
        t.timestamp = `${startDate} ${14 + i}:30`;
      });
    }

    console.log(`[API Get] getRecentTransactionsData for site ${siteId} (Dates: ${startDate} to ${endDate}) - Response:`, mockData);

    return of(mockData).pipe(
      delay(800),
      tap(res => console.log(`[API Log] RecentTransactionsData successfully loaded:`, res))
    );
  }

  /**
   * Fetches Monthly Parking Statistics from API
   * ดึงข้อมูลสรุปสถิติผู้เข้าจอดรายเดือนย้อนหลังตามช่วงเวลา
   */
  getMonthlyStatsData(siteId: string, startDate?: string, endDate?: string): Observable<MonthlyStatsDto> {
    const mockData: MonthlyStatsDto = {
      months: ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.'],
      parking_count: siteId === 'site-A' ? [1200, 1350, 1100, 1450, 1600, 1750] : siteId === 'site-B' ? [800, 920, 850, 990, 1050, 1120] : [1000, 1100, 980, 1200, 1300, 1400],
      ev_count: siteId === 'site-A' ? [150, 180, 210, 240, 290, 340] : siteId === 'site-B' ? [80, 95, 110, 130, 150, 180] : [110, 130, 160, 180, 220, 260],
      revenue_data: siteId === 'site-A' ? [250, 280, 240, 310, 330, 360] : siteId === 'site-B' ? [150, 180, 170, 200, 210, 230] : [200, 230, 210, 250, 270, 290] // ในหน่วยพันบาท (k Baht)
    };

    if (startDate && endDate) {
      // Simulate historical stats change
      mockData.revenue_data = mockData.revenue_data.map(v => Math.round(v * 0.92));
    }

    console.log(`[API Get] getMonthlyStatsData for site ${siteId} (Dates: ${startDate} to ${endDate}) - Response:`, mockData);

    return of(mockData).pipe(
      delay(800),
      tap(res => console.log(`[API Log] MonthlyStatsData successfully loaded:`, res))
    );
  }
}
